/**
 * `useVoiceDictation` — promptbox voice dictation (operator brief
 * 2026-07-02, ChatGPT-style flow): mic button → the composer becomes a
 * recording strip with a LIVE waveform while the user talks → ✗ discards,
 * ✓ stops the recorder and ships the clip to the runtime's LOCAL lx-stt
 * (`lx.stt_transcribe`, small whisper model — no audio egress, same
 * legal-confidentiality posture as the IM voice-note path) → the
 * transcript lands in the composer for review/edit before send.
 *
 * States: idle → recording → transcribing → idle (or error).
 * Waveform: an AnalyserNode samples the mic at ~30fps into a rolling
 * window of normalized amplitude levels (0..1) the strip renders as bars.
 *
 * Chrome's MediaRecorder emits `audio/webm;codecs=opus`; the runtime's
 * PyAV decoder handles the webm container (live-proven 2026-07-02).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useGatewayStore } from "@/stores/useGatewayStore";

export type DictationState = "idle" | "recording" | "transcribing" | "error";

/** Rolling number of waveform bars the strip renders. */
export const DICTATION_WAVE_BARS = 48;

/** Hard recording cap — dictation is for commands, not depositions. */
const MAX_RECORDING_MS = 5 * 60_000;

export interface VoiceDictation {
  state: DictationState;
  /** Rolling amplitude window (0..1 each), newest last — length ≤ DICTATION_WAVE_BARS. */
  levels: number[];
  /** ms since recording started (0 outside `recording`). */
  elapsedMs: number;
  /** Set when state === "error" (mic denied / STT failure). */
  error: string | null;
  /** True when the browser can record at all (mediaDevices + MediaRecorder). */
  supported: boolean;
  start: () => Promise<void>;
  /** ✓ — stop recording and transcribe. Resolves with the transcript
   *  ("" on failure) plus a session-local object URL of the recorded
   *  clip (for the voice-bubble's immediate playback; null on failure).
   *  Caller owns revoking the URL if it discards it. */
  accept: () => Promise<{ transcript: string; audioUrl: string | null }>;
  /** ✗ — discard the recording, no transcription. */
  cancel: () => void;
}

function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
  return candidates.find((c) => MediaRecorder.isTypeSupported(c)) ?? "";
}

/** Container extension for the runtime temp file, from the recorder mime. */
export function extForMime(mime: string): string {
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

export function useVoiceDictation(): VoiceDictation {
  const rpc = useGatewayStore((s) => s.rpc);
  const [state, setState] = useState<DictationState>("idle");
  const [levels, setLevels] = useState<number[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const canceledRef = useRef(false);

  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const teardown = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    setLevels([]);
    setElapsedMs(0);
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback(async () => {
    if (!supported || state === "recording" || state === "transcribing") return;
    setError(null);
    canceledRef.current = false;
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, {
        mimeType: pickMimeType() || undefined,
      });
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(250);
      startedAtRef.current = Date.now();
      setState("recording");

      // Live waveform: AnalyserNode time-domain RMS → rolling window.
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      let lastSample = 0;
      const tick = () => {
        const now = performance.now();
        if (now - lastSample > 33) {
          lastSample = now;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (const v of buf) {
            const d = (v - 128) / 128;
            sum += d * d;
          }
          // RMS ≈ 0..0.05 for quiet speech; scale into a visible 0..1 band.
          const level = Math.min(1, Math.sqrt(sum / buf.length) * 6);
          setLevels((prev) => [...prev.slice(-(DICTATION_WAVE_BARS - 1)), level]);
          setElapsedMs(Date.now() - startedAtRef.current);
          if (Date.now() - startedAtRef.current > MAX_RECORDING_MS) {
            recorderRef.current?.stop();
            return;
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      teardown();
      setError((err as Error).message);
      setState("error");
    }
  }, [supported, state, teardown]);

  /** Stop the recorder and resolve with the final Blob. */
  const stopRecorder = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve(null);
        return;
      }
      const mime = recorder.mimeType || "audio/webm";
      recorder.onstop = () => {
        resolve(chunksRef.current.length ? new Blob(chunksRef.current, { type: mime }) : null);
      };
      recorder.stop();
    });
  }, []);

  const cancel = useCallback(() => {
    canceledRef.current = true;
    recorderRef.current?.stop();
    teardown();
    setState("idle");
  }, [teardown]);

  const accept = useCallback(async (): Promise<{
    transcript: string;
    audioUrl: string | null;
  }> => {
    if (state !== "recording") return { transcript: "", audioUrl: null };
    const blob = await stopRecorder();
    teardown();
    if (canceledRef.current || !blob || !rpc) {
      setState("idle");
      return { transcript: "", audioUrl: null };
    }
    setState("transcribing");
    try {
      const bytes = new Uint8Array(await blob.arrayBuffer());
      let binary = "";
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const { transcript } = await rpc.lxSttTranscribe({
        audioBase64: btoa(binary),
        ext: extForMime(blob.type),
      });
      setState("idle");
      return { transcript, audioUrl: URL.createObjectURL(blob) };
    } catch (err) {
      setError((err as Error).message);
      setState("error");
      return { transcript: "", audioUrl: null };
    }
  }, [state, stopRecorder, teardown, rpc]);

  return { state, levels, elapsedMs, error, supported, start, accept, cancel };
}
